import React from 'react';
import styled from 'styled-components';
import { theme } from '../../theme';

const ServicesContainer = styled.div`
  padding: ${theme.spacing.section} 0;
  background-color: ${theme.colors.primary};
  background-image: linear-gradient(to bottom, ${theme.colors.primary}, ${theme.colors.primaryDark});
  color: ${theme.colors.textLight};
`;

const ServicesWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ServicesHeading = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 64px;
  font-family: ${theme.fonts.heading};
  position: relative;
  text-align: center;
  
  @media screen and (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 48px;
  }
  
  @media screen and (max-width: 480px) {
    font-size: 1.75rem;
  }
  
  &:after {
    content: '';
    position: absolute;
    width: 80px;
    height: 3px;
    background-color: ${theme.colors.accent};
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  grid-gap: 30px;
  width: 100%;
  
  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(5px);
  padding: 30px;
  border-radius: ${theme.borderRadius};
  box-shadow: ${theme.boxShadow};
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-10px);
    background: rgba(255, 255, 255, 0.15);
  }
`;

const ServiceIcon = styled.div`
  width: 70px;
  height: 70px;
  background: ${theme.colors.accent};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  font-size: 2rem;
  font-weight: bold;
`;

const ServiceTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 15px;
  color: ${theme.colors.textLight};
  font-weight: 700;
`;

const ServiceDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: ${theme.colors.textLight};
  flex-grow: 1;
`;

const LearnMoreButton = styled.a`
  display: inline-block;
  margin-top: 20px;
  padding: 10px 0;
  color: ${theme.colors.accent};
  font-weight: 600;
  text-decoration: none;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    width: 100%;
    height: 2px;
    bottom: 5px;
    left: 0;
    background-color: ${theme.colors.accent};
    transform: scaleX(0);
    transform-origin: bottom right;
    transition: transform 0.3s;
  }
  
  &:hover:after {
    transform: scaleX(1);
    transform-origin: bottom left;
  }
`;

const ServicesSection = () => {
  return (
    <ServicesContainer id="services">
      <ServicesWrapper>
        <ServicesHeading>What We Offer</ServicesHeading>
        <ServicesGrid>
          <ServiceCard>
            <ServiceIcon>1</ServiceIcon>
            <ServiceTitle>1:1 Wilderness Psychotherapy</ServiceTitle>
            <ServiceDescription>
              Sessions in nature, indoors or online, tailored to your pace and your needs. 
              Experience the healing power of therapeutic conversations in natural settings.
            </ServiceDescription>
            <LearnMoreButton 
              href="#" 
              onClick={(e) => {
                e.preventDefault();
                if (window.navigateTo) window.navigateTo('wilderness-therapy');
              }}
            >
              Learn More
            </LearnMoreButton>
          </ServiceCard>
          
          <ServiceCard>
            <ServiceIcon>2</ServiceIcon>
            <ServiceTitle>Group Wilderness Therapy</ServiceTitle>
            <ServiceDescription>
              4–8 week programmes for schools, charities and organisations. Bespoke. 
              Trauma-informed. Always person-centred. Build resilience and connection in community.
            </ServiceDescription>
            <LearnMoreButton 
              href="#" 
              onClick={(e) => {
                e.preventDefault();
                if (window.navigateTo) window.navigateTo('group-wilderness-therapy');
              }}
            >
              Learn More
            </LearnMoreButton>
          </ServiceCard>
          
          <ServiceCard>
            <ServiceIcon>3</ServiceIcon>
            <ServiceTitle>WildStrong Movement Coaching</ServiceTitle>
            <ServiceDescription>
              Mobility and strength training that honours your body's story. Outside, barefoot, 
              functional. Available as drop-ins, block programmes, or online sessions.
            </ServiceDescription>
            <LearnMoreButton 
              href="#" 
              onClick={(e) => {
                e.preventDefault();
                if (window.navigateTo) window.navigateTo('movement-coaching');
              }}
            >
              Learn More
            </LearnMoreButton>
          </ServiceCard>
          
          <ServiceCard>
            <ServiceIcon>4</ServiceIcon>
            <ServiceTitle>Retreats and Journeys</ServiceTitle>
            <ServiceDescription>
              Facilitating people's journey through nature and the wilderness of their mind on water 
              and on land. Reconnect, rebuild, and remember who you are when you're not just surviving.
            </ServiceDescription>
            <LearnMoreButton href="#contact">Learn More</LearnMoreButton>
          </ServiceCard>
          <ServiceCard>
            <ServiceIcon>5</ServiceIcon>
            <ServiceTitle>Corporate Wellbeing</ServiceTitle>
            <ServiceDescription>
              Team building and wellbeing experiences for organizations. Reduce burnout, improve staff wellbeing, 
              and strengthen workplace culture through nature-based interventions.
            </ServiceDescription>
            <LearnMoreButton 
              href="#" 
              onClick={(e) => {
                e.preventDefault();
                if (window.navigateTo) window.navigateTo('corporate-wellbeing');
              }}
            >
              Learn More
            </LearnMoreButton>
          </ServiceCard>
        </ServicesGrid>
      </ServicesWrapper>
    </ServicesContainer>
  );
};

export default ServicesSection;